import type { FieldPolicy, FieldReadFunction, Reference, TypePolicy } from '@apollo/client'
import { isOptimisticId, OPTIMISTIC_SEQ_BASE } from '@sections/messaging/constants'

// `customers` and `users` keep a read that always returns undefined, so both
// lists always miss the cache and refetch. That is observable behaviour — the
// two entries below are deliberate. The unreachable `USE_FETCH_MORE`
// fall-through to `relayStylePagination` is gone; nothing in any repo set it.
function relayStylePaginationOptional(): FieldReadFunction {
  return () => undefined
}

/**
 * The messaging contract is NOT Relay-shaped — `conversations` and
 * `conversationMessages` return `{ items, hasMore, nextCursor }`, so
 * `relayStylePagination` cannot consume them. These custom merge policies
 * implement cursor pagination + de-dup tailored to that shape.
 */

// Inbox: union pages, de-dup by conversation `id`. The list is re-sorted
// client-side (by lastActivityAt) in useConversationList, so merge only needs
// to keep every conversation present once.
const conversationsPolicy: FieldPolicy = {
  // `scope` (My inbox vs the Unassigned triage queue) and `includeDismissed`
  // (whether spam-dropped conversations are listed) each partition the result
  // set into a disjoint membership — they must key separate cache buckets or
  // the merge below would union the two queues / leak dismissed rows back in
  // when toggled off. For an own-inbox support viewer both scopes resolve
  // operatorId=undefined, so without `scope` here the queues would collide.
  keyArgs: ['lifecycle', 'operatorId', 'scope', 'includeDismissed'],
  merge(existing, incoming, { readField }) {
    const byId = new Map<unknown, Reference>()
    for (const item of [
      ...((existing?.items as Reference[]) ?? []),
      ...((incoming?.items as Reference[]) ?? []),
    ]) {
      byId.set(readField('id', item), item)
    }

    return {
      ...incoming,
      items: Array.from(byId.values()),
    }
  },
}

// Thread: OLDER prepends history, NEWER appends; de-dup by `clientMsgId` (so an
// optimistic bubble is replaced by its reconciled server message) falling back
// to `id`, then order by (`createdAt`, `seq`). `createdAt` is the message's
// order stamp (an inbound's ingress receipt, capped at the customer's send
// second — DECISIONS D5): the Pub/Sub lanes are unordered, so `seq`, which
// messaging-core assigns in ARRIVAL order, is the pagination cursor and the
// live-push watermark but not the render order — a backlog replayed after an
// outage lands with high seqs and old createdAts, and must still read in the
// order the customer wrote it. Optimistic bubbles sort LAST by rule, not by
// clock: they carry the browser's clock, which may sit behind the server's,
// and the operator's own just-sent bubble must never render mid-thread. The
// OLDER cursor/hasMore drives "load older history".
const conversationMessagesPolicy: FieldPolicy = {
  keyArgs: ['conversationId'],
  merge(existing, incoming, { args, readField }) {
    const direction = args?.direction
    const existingItems = (existing?.items as Reference[]) ?? []
    const incomingItems = (incoming?.items as Reference[]) ?? []
    const ordered =
      direction === 'OLDER'
        ? [...incomingItems, ...existingItems]
        : [...existingItems, ...incomingItems]

    const byKey = new Map<unknown, Reference>()
    for (const item of ordered) {
      const key = readField('clientMsgId', item) ?? readField('id', item)
      const current = byKey.get(key)
      if (current === undefined) {
        byKey.set(key, item)
        continue
      }

      // Reconcile: a real (non-optimistic) message replaces its optimistic twin.
      if (isOptimisticId(readField('id', current)) && !isOptimisticId(readField('id', item))) {
        byKey.set(key, item)
      }
    }

    const items = Array.from(byKey.values()).sort((a, b) => {
      const seqA = (readField('seq', a) as number | null) ?? OPTIMISTIC_SEQ_BASE
      const seqB = (readField('seq', b) as number | null) ?? OPTIMISTIC_SEQ_BASE
      const isOptimisticA = seqA >= OPTIMISTIC_SEQ_BASE
      const isOptimisticB = seqB >= OPTIMISTIC_SEQ_BASE
      if (isOptimisticA !== isOptimisticB) {
        return isOptimisticA ? 1 : -1
      }

      const atA = Date.parse((readField('createdAt', a) as string | null) ?? '') || 0
      const atB = Date.parse((readField('createdAt', b) as string | null) ?? '') || 0
      if (atA !== atB) {
        return atA - atB
      }

      return seqA - seqB
    })

    // Keep the OLDER-history cursor as the paginating cursor.
    const meta =
      direction === 'NEWER'
        ? {
            hasMore: existing?.hasMore ?? incoming?.hasMore ?? false,
            nextCursor: existing?.nextCursor ?? incoming?.nextCursor ?? null,
          }
        : {
            hasMore: incoming?.hasMore ?? false,
            nextCursor: incoming?.nextCursor ?? null,
          }

    return {
      conversationId: incoming?.conversationId ?? existing?.conversationId ?? null,
      ...meta,
      items,
    }
  },
}

// Import quarantine. Relay-shaped, so `relayStylePagination` would consume it
// — but it would also UNION every page forever, which is wrong for a work
// queue: a lead that leaves the queue (retried, or repaired into a case) has
// to disappear from the list, and the refetch that would remove it re-runs the
// query with its ORIGINAL variables (`after: undefined`). Under a plain union,
// rows past the first page could never be removed by any refresh at all, and a
// row the refetched first page omits would linger, still visible and
// actionable.
//
// So: a call with no `after` (initial load AND every `refetchQueries: 'active'`
// refresh after a retry/repair) REPLACES the cached value — the fresh first
// page is authoritative. A `fetchMore` call (`after` set) MERGES, de-duped on
// the node's id, because a row can legitimately reappear across pages when the
// rows in front of it are being removed underneath the traversal cursor.
//
// `keyArgs` keeps `filter` and `sort`: each combination selects a DIFFERENT set
// of rows, and collapsing
// them onto one cache entry would show a stage-filtered page the leads it had
// just filtered out.
const caseImportQuarantinesPolicy: FieldPolicy = {
  keyArgs: ['filter', 'sort'],
  merge(existing, incoming, { args, readField }) {
    const isFetchMore = args?.after != null
    if (!isFetchMore || !existing) {
      return incoming
    }

    const byNodeId = new Map<unknown, Reference>()
    for (const edge of [
      ...((existing?.edges as Reference[]) ?? []),
      ...((incoming?.edges as Reference[]) ?? []),
    ]) {
      const node = readField('node', edge) as Reference | undefined
      byNodeId.set(node ? readField('id', node) : edge, edge)
    }

    return {
      ...incoming,
      edges: Array.from(byNodeId.values()),
    }
  },
}

const QueryPolicies: TypePolicy = {
  fields: {
    caseImportQuarantines: caseImportQuarantinesPolicy,
    conversationMessages: conversationMessagesPolicy,
    conversations: conversationsPolicy,
    customers: relayStylePaginationOptional(),
    users: relayStylePaginationOptional(),
  },
}

export default QueryPolicies
